import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  TextField,
  FormControl,
  FormLabel,
  Box,
  Autocomplete,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  styled,
  tooltipClasses,
  TooltipProps,
  Checkbox,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import {
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormTrigger,
  UseFormWatch,
  UseFormSetError,
  UseFormSetValue,
  UseFormRegister,
  RegisterOptions,
} from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { FormTypes } from "../Types";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Loading from "./Loading";
import {
  formatAckStatusDateDisplay,
  isISODateTimeLike,
  isUtcDateTimeDisplayField,
  isXevmpdFormDateTimeDisplayField,
} from "../assets/utils";

interface FormFieldRendererProps {
  formField: any;
  control: any;
  watch: UseFormWatch<FormTypes>;
  isNotDraftState: boolean;
  errors: FieldErrors<FormTypes>;
  trigger: UseFormTrigger<FormTypes>;
  clearErrors: UseFormClearErrors<FormTypes>;
  register: UseFormRegister<any>;
  setError: UseFormSetError<FormTypes>;
  setValue: UseFormSetValue<FormTypes>;
  intialformData: any;
  formMode: "view" | "edit" | "add";
  options?: { label: string; value: string }[];
  firstEditableFieldName: string;
}

const LargeScrollTooltip = styled((props: TooltipProps) => (
  <Tooltip {...props} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    overflowY: "auto",
    whiteSpace: "normal",
    fontSize: "0.875rem",
  },
}));

const TooltipTextField = (props: any) => {
  return (
    <LargeScrollTooltip
      title={props?.inputProps?.value || ""}
      disableInteractive={false}
      placement="bottom-start"
      PopperProps={{ disablePortal: true }}
    >
      <TextField {...props} />
    </LargeScrollTooltip>
  );
};

const FormFieldRenderer: React.FC<FormFieldRendererProps> = ({
  formField,
  control,
  watch,
  isNotDraftState,
  errors,
  register,
  trigger,
  clearErrors,
  setError,
  setValue,
  intialformData,
  formMode,
  options,
  firstEditableFieldName
}) => {
  const { t } = useTranslation();
  const value = intialformData?.[formField.field] ?? "";

  // Use watch only for file field to get current form value
  const fieldValue = watch(formField.field as keyof FormTypes);

  const [progress, setProgress] = useState<number>(0);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isFileInitialized, setIsFileInitialized] = useState(false);
  const [lastDraftVersion, setLastDraftVersion] = useState<string>("0.1");
  const [open, setOpen] = useState(false);

  // Watch operationType to make the disabled state reactive
  const operationType = watch("operationType");

  // Watch authorizationStatus for previousEVCode mandatory logic
  const watchedAuthStatus =  intialformData["authorizationStatus"]//watch("authorizationStatus" as keyof FormTypes);

  // Helper function to get operation type value (handles both object and string formats)
  const getOperationTypeValue = () => {
    if (operationType?.value) return operationType.value;
    if (typeof operationType === "string") return operationType;
    if (intialformData?.operationType?.value)
      return intialformData.operationType.value;
    if (typeof intialformData?.operationType === "string")
      return intialformData.operationType;
    return null;
  };

  // Helper function to get authorizationStatus value (handles both object and string formats)
  const getAuthStatusValue = () => {
    if ((watchedAuthStatus as any)?.value) return (watchedAuthStatus as any).value;
    if (typeof watchedAuthStatus === "string") return watchedAuthStatus;
    if (intialformData?.authorizationStatus)
      return intialformData.authorizationStatus;
    if (typeof intialformData?.authorizationStatus === "string")
      return intialformData.authorizationStatus;
    return null;
  };

  // Autofocus logic additions
  const firstEditableRendered = useRef(false);
  useEffect(() => { firstEditableRendered.current = false; }, [formMode, intialformData]);
  // Inside FormFieldRenderer, before the switch or at the top of the component
const eVCode = watch("eVCode");

// Filter options based on eVCode
const filteredOptions = useMemo(() => {
  if (!options) return [];
  if (eVCode) {
    return options.filter(opt => opt.value !== "Insert");
  }
  return options;
}, [options, eVCode]);

// Clear operationType if it was "Insert" and eVCode now has a value
useEffect(() => {
  if (!eVCode) return; // no need to clear when eVCode is empty

  const opValue = getOperationTypeValue(); // use your existing helper
  if (opValue === "Insert") {
    setValue("operationType", null);
  }
}, [eVCode, operationType, setValue]); // include dependencies
// Add this ref
const isInitialMount = useRef(true);

// Replace your useEffect
useEffect(() => {
  if (isInitialMount.current) {
    isInitialMount.current = false;
    return;
  }
  // trigger("eVCode");
  trigger("comments" as keyof FormTypes);
  trigger("previousEVCode" as keyof FormTypes);
}, [operationType, trigger]);

// Re-validate previousEVCode when authorizationStatus changes
// useEffect(() => {
//   if (isInitialMount.current) return;
//   trigger("previousEVCode" as keyof FormTypes);
// }, [watchedAuthStatus, trigger]);


  const operationTypeValue = getOperationTypeValue();
  const authStatusValue = getAuthStatusValue();

  // Determine if the field should actually be required
  const isActuallyRequired =
    (formField?.required &&
      !(formField?.field === "eVCode" && (operationTypeValue === "Insert" || operationTypeValue === "Invalidate"))) ||
    (formField?.field === "comments" && operationTypeValue === "Nullify") ||
    (formField?.field === "previousEVCode" &&
      (authStatusValue === "Valid - Transferred Marketing Authorisation" ||
       authStatusValue === "Valid - Renewed Marketing Authorisation"));

  // Check if formField has description
  const hasDescription = Boolean(formField?.description);

  // Helper function to get filename without extension
  const getFileNameWithoutExtension = (filename: string) => {
    if (!filename) return "";
    return filename.replace(/\.[^/.]+$/, "");
  };

  // Helper function to get file extension
  const getFileExtension = (filename: string) => {
    if (!filename) return "";
    return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
  };

  // Get current character count for text fields
  const getCurrentCharacterCount = () => {
    if (formField?.type !== "text") return 0;
    const currentValue = watch(formField.field as keyof FormTypes);
    if (typeof currentValue === "string") {
      return currentValue.length;
    }
    return 0;
  };
  const characterCount = getCurrentCharacterCount();
  const maxLength = formField?.maxLen;

  useEffect(() => {
    if (formField?.type === "file") {
      // For file field, use the current form value from watch
      const currentFileValue = fieldValue;

      // Type guard to check if it's a file object
      const isFileObject = (
        value: any
      ): value is { name?: string; data?: any; rawFile?: any } => {
        return (
          value &&
          typeof value === "object" &&
          !(value instanceof Date) &&
          !("value" in value)
        );
      };

      if (
        currentFileValue &&
        !isFileInitialized &&
        isFileObject(currentFileValue)
      ) {
        // Check if currentFileValue has file data
        const hasFileData = currentFileValue.name || currentFileValue.rawFile;

        if (hasFileData) {
          setUploadedFile(currentFileValue);
          setProgress(100);

          // Set attachmentName without file extension
          if (currentFileValue.name) {
            const fileNameWithoutExt = getFileNameWithoutExtension(
              currentFileValue.name
            );
            setValue("attachmentName", fileNameWithoutExt, {
              shouldValidate: true,
            });
          }

          setIsFileInitialized(true);
        }
      }
    }
  }, [formMode, formField?.type, fieldValue, setValue, isFileInitialized]);

  // Reset file state when form value becomes empty
  useEffect(() => {
    if (formField?.type === "file" && !fieldValue && uploadedFile) {
      setUploadedFile(null);
      setProgress(0);
      setIsFileInitialized(false);
    }
  }, [fieldValue, uploadedFile, formField?.type]);

  // Update file name when attachmentName changes
  useEffect(() => {
    if (formField?.type === "file" && uploadedFile) {
      const attachmentName = watch("attachmentName" as keyof FormTypes);
      if (attachmentName && typeof attachmentName === "string") {
        // Get original file extension
        const originalExtension = getFileExtension(uploadedFile.name);

        // Create new filename with extension
        const newFileName = originalExtension
          ? `${attachmentName}.${originalExtension}`
          : attachmentName;

        // Only update if the name actually changed
        if (newFileName !== uploadedFile.name) {
         const updatedFile = {
           ...uploadedFile,
           name: newFileName,
         };
         setUploadedFile(updatedFile);
         setValue(formField.field as keyof FormTypes, updatedFile, {
           shouldValidate: true,
         });
        }
      }
    }
  }, [watch("attachmentName"), uploadedFile, formField, setValue]);

  // Set checkbox default value to "No" when field is added
  useEffect(() => {
    if (formField?.type === "checkbox" && formMode === "add" && !fieldValue) {
      setValue(formField.field as keyof FormTypes, "No", {
        shouldValidate: true,
      });
    }
  }, [formField, formMode, fieldValue, setValue]);

  if (formField?.hide) return null;

  // Updated isDisabled condition - now reactive to operationType changes
  const isDisabled =
    formMode === "view" ||
    formField?.disabled ||
    isNotDraftState ||
    (formField?.field === "previousEVCode" &&
      getOperationTypeValue() === "Insert");

  const getInitialSelectValue = () => {
    if (formMode !== "edit") return value;

    if (formField.type !== "select") return value;

    if (typeof value === "string" || typeof value === "number") {
      return options?.find((opt) => opt.value === value) || null;
    }

    if (value && typeof value === "object" && "value" in value) {
      return (
        options?.find((opt) => opt.value === (value as any).value) || value
      );
    }

    return value || null;
  };

  const getFieldComponent = (field: any) => {
    // Autofocus on only the first editable (not disabled) field
    const shouldAutoFocus = formField.field === firstEditableFieldName;

    switch (formField?.type) {
      case "radio": {
          if (formField.asyncConfig?.url && formField.isLoading) {
          return <Loading open={true} />;
        }
        return (
          <Box>
            <RadioGroup
              {...field}
              row
              value={field.value ?? ""}
              onChange={(event) => {
                const newValue = event.target.value;
                field.onChange(newValue);
                clearErrors(formField?.field as keyof FormTypes);
                trigger(formField?.field as keyof FormTypes);
              }}
            >
              {(options ?? []).map((option: any) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={
                    <Radio
                      disabled={isDisabled}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {option.label}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          </Box>
        );
      }

      case "checkbox": {
        // ✅ Get current field value, default to "No" if empty/undefined
        const currentCheckboxValue = field.value ?? "No";
        
        return (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentCheckboxValue === "Yes"}
                  disabled={isDisabled}
                  size="small"
                  onChange={(event) => {
                    const checked = event.target.checked;
                    const newValue = checked ? "Yes" : "No"; // ✅ Always "Yes" or "No"
                    field.onChange(newValue);
                    clearErrors(formField?.field as keyof FormTypes);
                    trigger(formField?.field as keyof FormTypes);
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  {/* {formField?.label || t(formField?.field)} */}
                </Typography>
              }
            />
            {/* Show validation error for checkbox */}
            {errors[formField?.field as keyof FormTypes] && (
              <FormHelperText error>
                {errors[formField?.field as keyof FormTypes]?.message}
              </FormHelperText>
            )}
          </Box>
        );
      }

      case "number": {
        return (
          <LargeScrollTooltip
            title={field.value || ""}
            placement="bottom-start"
            disableInteractive={false}
            PopperProps={{ disablePortal: true }}
          >
            <TextField
              {...field}
              type="number"
              size="small"
              placeholder={formField?.placeholder}
              disabled={isDisabled}
              error={!!errors[formField?.field as keyof FormTypes]}
              helperText={errors[formField?.field as keyof FormTypes]?.message || ""}
              InputProps={{
                inputProps: {
                  min: formField?.min,
                  max: formField?.max,
                  step: formField?.step || 1,
                },
              }}
              onBlur={() => trigger([formField?.field as keyof FormTypes])}
              onChange={(event) => {
                const value = event.target.value;
                // Allow empty string for clearing
                field.onChange(value === "" ? null : Number(value));
                clearErrors([formField?.field as keyof FormTypes]);
              }}
              autoFocus={shouldAutoFocus}
            />
          </LargeScrollTooltip>
        );
      }

      case "text": {
        const isUtcDateField =
          isUtcDateTimeDisplayField(formField.field) ||
          isXevmpdFormDateTimeDisplayField(formField.field);
        const isAckStatusIso =
          isUtcDateField && isISODateTimeLike(field.value ?? value);
        const textValue = isAckStatusIso
          ? formatAckStatusDateDisplay(field.value ?? value)
          : (field.value ?? value);
        return (
          <LargeScrollTooltip
            title={textValue || ""}
            placement="bottom-start"
            disableInteractive={false}
            disableFocusListener
            disableTouchListener
            enterDelay={300}
            enterNextDelay={100}
            PopperProps={{ disablePortal: true }}
          >
            <TextField
              {...field}
              size="small"
              placeholder={
                formField.field !== "attachmentName"
                  ? formField?.placeholder
                  : undefined
              }
              disabled={isDisabled}
              defaultValue={value}
              value={textValue}
              error={!!errors[formField?.field as keyof FormTypes]}
              helperText={
                errors[formField?.field as keyof FormTypes]?.message || ""
              }
              multiline={formField.textArea}
              rows={formField.maxRows}
              onBlur={() => trigger([formField?.field as keyof FormTypes])}
              InputProps={{
                readOnly: isAckStatusIso,
                inputProps: { maxLength: formField.maxLen },
              }}
              onChange={(event) => {
                if (isAckStatusIso) return;
                field.onChange(event.target.value);
                clearErrors([formField?.field as keyof FormTypes]);
              }}
              autoFocus={shouldAutoFocus}
            />
          </LargeScrollTooltip>
        );
      }

      case "select": {
        // Get initial value for select field
        const initialSelectValue = getInitialSelectValue();
        
        // Check if it's multi-select
        const isMultiSelect = formField?.multiple === true;
        
        // Handle current value properly for multi-select
        let currentValue = field.value;
        if (isMultiSelect) {
          // For multi-select, ensure we have an array
          if (!Array.isArray(currentValue)) {
            currentValue = [];
          }
          // Convert string values to objects if needed
          currentValue = (currentValue || []).map((val: any) => {
            if (typeof val === 'string' || typeof val === 'number') {
              return options?.find((opt: any) => opt.value === val) || { value: val, label: val };
            }
            return val;
          });
        } else {
          // For single select — rematch against async options so label comes from KM.
          if (typeof currentValue === "string" || typeof currentValue === "number") {
            currentValue =
              options?.find((opt: any) => opt.value === currentValue) ||
              (currentValue
                ? { value: currentValue, label: currentValue }
                : null);
          } else if (
            currentValue &&
            typeof currentValue === "object" &&
            "value" in currentValue
          ) {
            currentValue =
              options?.find((opt: any) => opt.value === currentValue.value) ||
              currentValue;
          }
        }
        
        return (
          <Autocomplete
            multiple={isMultiSelect}
            options={filteredOptions ?? []}
            getOptionLabel={(option: any) => {
              if (!option) return '';
              if (typeof option === 'object') return option.label || option.value || '';
              return String(option || '');
            }}
            isOptionEqualToValue={(option: any, value: any) => {
              if (!option || !value) return false;
              const optionValue = typeof option === 'object' ? option.value : option;
              const valueValue = typeof value === 'object' ? value.value : value;
              return optionValue === valueValue;
            }}
            value={currentValue}
            disabled={isDisabled}
            renderInput={(params) => (
              <LargeScrollTooltip
                title={Array.isArray(currentValue) 
                  ? currentValue.map((v: any) => v?.label || v?.value || v).join(', ') 
                  : currentValue?.label || currentValue?.value || ""}
                placement="bottom-start"
                disableInteractive={false}
                PopperProps={{ container: document.body }}
              >
                <TextField
                  {...params}
                  placeholder={formField?.placeholder}
                  error={!!errors[formField?.field as keyof FormTypes]}
                  helperText={
                    errors[formField?.field as keyof FormTypes]?.message || ""
                  }
                  size="small"
                  autoFocus={shouldAutoFocus}
                />
              </LargeScrollTooltip>
            )}
            renderOption={(props, option: any) => (
              <MenuItem {...props} value={option.value} style={{ whiteSpace: 'normal' }}>
                {option.label}
              </MenuItem>
            )}
            onChange={(_, value) => {
              if (isMultiSelect) {
                // For multi-select, map to values
                const selectedValues = (value || []).map((item: any) => 
                  typeof item === 'object' ? item.value : item
                );
                field.onChange(selectedValues);
              } else {
                // For single select
                const selectedValue = value ? 
                  (typeof value === 'object' ? value.value : value) : 
                  null;
                field.onChange(selectedValue);
              }
              clearErrors(formField?.field as keyof FormTypes);
              trigger(formField?.field as keyof FormTypes);

              // Handle xevmpdStatus version logic
              if (formField?.field === "xevmpdStatus" && !isMultiSelect) {
                const selectedObj = value as any;
                const currentVersionRaw = watch("version");
                const parsed =
                  typeof currentVersionRaw === "number"
                    ? currentVersionRaw
                    : parseFloat(String(currentVersionRaw ?? "0"));
                const currentVersion = Number.isFinite(parsed) ? parsed : 0;

                if (selectedObj?.value === "Draft") {
                  // Draft → restore previous draft version if available
                  const next =
                    !currentVersion ||
                    isNaN(currentVersion) ||
                    currentVersion === 0
                      ? 0.1
                      : Number(lastDraftVersion);
                  setValue("version", next.toFixed(1), {
                    shouldValidate: true,
                  });
                }

                if (selectedObj?.value === "Submitted") {
                  // Save current version before bump (for restoring later)
                  setLastDraftVersion(currentVersion.toFixed(1));

                  // If no version yet → start from 1.0, else bump +0.9
                  const next =
                    !currentVersion ||
                    isNaN(currentVersion) ||
                    currentVersion === 0
                      ? 1.0
                      : Number((currentVersion + 0.9).toFixed(1));
                  setValue("version", next.toFixed(1), {
                    shouldValidate: true,
                  });
                }
              }
              if (formField?.field === "operationType") {
          //  trigger("eVCode");
           trigger("comments" as keyof FormTypes);
           trigger("previousEVCode" as keyof FormTypes);
            }
              if (formField?.field === "authorizationStatus") {
                trigger("previousEVCode" as keyof FormTypes);
              }
            }}
          />
        );
      }

      case "date": {
        const dateFormat = formField?.["ui:dateFormat"] || "DD/MMM/YYYY";
        if (
          (isUtcDateTimeDisplayField(formField?.field) ||
            isXevmpdFormDateTimeDisplayField(formField?.field)) &&
          isISODateTimeLike(field.value)
        ) {
          const displayValue = formatAckStatusDateDisplay(field.value);
          return (
            <TextField
              size="small"
              fullWidth
              disabled={isDisabled}
              value={displayValue ?? ""}
              placeholder={formField?.placeholder}
              InputProps={{ readOnly: true }}
            />
          );
        }

        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={field.value ? dayjs(field.value, dateFormat) : null}
              onChange={(date) => {
                const dateValue = date ? date.format(dateFormat) : null;
                field.onChange(dateValue);
                clearErrors(formField?.field as keyof FormTypes);
                trigger(formField?.field as keyof FormTypes);
              }}
              open={open}
              onOpen={() => {
                if (!isDisabled) setOpen(true);
              }}
              onClose={() => setOpen(false)}
              format={dateFormat}
              disableFuture={formField.field === "statusDate"}
              // Use slotProps and slots for MUI v6+
              slots={{ textField: TooltipTextField }}
              slotProps={{
                textField: {
                  error: !!errors[formField?.field as keyof FormTypes],
                  helperText: errors[formField?.field as keyof FormTypes]?.message || "",
                  size: "small",
                  placeholder: formField?.placeholder,
                  inputProps: {
                    value: field.value ? dayjs(field.value, dateFormat).format(dateFormat) : "",
                    readOnly: true, // Prevent manual typing
                    placeholder: formField?.placeholder,
                  },
                  onClick: () => {
                    if (!isDisabled) setOpen(true);
                  },
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {field.value && (
                          <HighlightOffOutlinedIcon
                            onClick={(ev) => {
                              ev.preventDefault();
                              ev.stopPropagation();
                              if (!isDisabled) {
                                field.onChange(null);
                              }
                            }}
                            sx={{
                              marginX: "5px",
                              fontSize: "14px",
                              color: "GrayText",
                              "&:hover": isDisabled ? {} : {
                                color: "Highlight",
                                cursor: "pointer",
                              },
                            }}
                          />
                        )}
                        <IconButton size="small" disabled={isDisabled}>
                          <CalendarMonthOutlinedIcon
                            sx={{
                              fontSize: "16px",
                              color: "GrayText",
                              "&:hover": {
                                color: "Highlight",
                              },
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  },
                }
              }}
              closeOnSelect={true}
              disabled={isDisabled}
            />
          </LocalizationProvider>
        );
      }

      case "file": {
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
        const supportedFileFormats = formField.supportedFormats || [
          "PDF",
          "DOC",
          "DOCX",
          "JPEG",
          "XLSX",
          "XML",
          "JSON",
          "RTF",
          "ZIP",
        ];

        // Helper function to check if file format is supported
        const isFileFormatSupported = (fileName: string) => {
          const fileExtension = fileName.split(".").pop()?.toUpperCase();
          return fileExtension
            ? supportedFileFormats.includes(fileExtension)
            : false;
        };

        const handleFileUpload = (file: File) => {
          // Check file format first
          if (!isFileFormatSupported(file.name)) {
            setUploadedFile(null);
            setProgress(0);
            field.onChange(null);

            setError(formField?.field as keyof FormTypes, {
              type: "manual",
              message: `Unsupported file format. Supported formats: ${supportedFileFormats.join(", ").toLowerCase()}`,
            });
            return;
          }

          // Then check file size
          if (file.size > MAX_FILE_SIZE) {
            setUploadedFile(null);
            setProgress(0);
            field.onChange(null);

            setError(formField?.field as keyof FormTypes, {
              type: "manual",
              message: "File size should not exceed 5MB",
            });
            return;
          }

          const reader = new FileReader();
          reader.onloadend = () => {
            const fileDetails = {
              name: file.name,
              size: file.size,
              rawFile: file,
            };

            field.onChange(fileDetails);
            setUploadedFile(fileDetails);

            // Set attachmentName without file extension
            const fileNameWithoutExt = getFileNameWithoutExtension(file.name);
            setValue("attachmentName", fileNameWithoutExt, {
              shouldValidate: true,
            });

            // Simulate upload progress
            setProgress(0);
            const interval = setInterval(() => {
              setProgress((old) => {
                if (old >= 100) {
                  clearInterval(interval);
                  return 100;
                }
                return old + 10;
              });
            }, 150);

            clearErrors(formField?.field as keyof FormTypes);
            trigger(formField?.field as keyof FormTypes);
          };
          reader.readAsDataURL(file);
        };

        const handleFileRemove = () => {
          setUploadedFile(null);
          setProgress(0);
          field.onChange(null);
          setValue("attachmentName", "");
          if (fileInputRef.current) fileInputRef.current.value = "";
          setIsFileInitialized(false);
          clearErrors(formField?.field as keyof FormTypes);
        };

        return (
          <Box
            border="1px dashed #ccc"
            borderRadius="8px"
            p={2}
            textAlign="center"
            sx={{
              cursor: isDisabled ? "not-allowed" : "pointer",
              backgroundColor: "#fafafa",
            }}
          >
            {/* Hidden input */}
            <input
              type="file"
              ref={fileInputRef}
              hidden
              disabled={isDisabled}
              accept={supportedFileFormats
                .map((f: any) => "." + f.toLowerCase())
                .join(",")}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file);
              }}
            />

            {/* Show upload area only if no file is uploaded */}
            
              <Box>
                <FileUploadOutlinedIcon sx={{ fontSize: 40, color: "#999" }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t("Drag & drop file here or")}{" "}
                  <Typography
                    component="span"
                    color="primary"
                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t("choose file")}
                  </Typography>
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {t("Supported file formats: ")}
                  {supportedFileFormats.join(", ").toLowerCase()}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {t("Max size: 5MB")}
                </Typography>
              </Box>
            
            {/* Uploaded file display */}
            {uploadedFile && (
              <Box textAlign="left" mt={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="body2">
                      {uploadedFile.name} (
                      {uploadedFile.size / 1024 < 1024
                        ? `${(uploadedFile.size / 1024).toFixed(1)} KB`
                        : `${(uploadedFile.size / (1024 * 1024)).toFixed(1)} MB`}
                    )
                    </Typography>
                  </Box>
                  {!isDisabled && (
                    <Button onClick={handleFileRemove}>
                      <DeleteOutlineIcon />
                    </Button>
                  )}
                </Box>

                {/* Progress bar - always show 100% for existing files */}
                <Box mt={1}>
                  <Box
                    sx={{
                      height: 6,
                      borderRadius: 5,
                      backgroundColor: "#eee",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${progress}%`,
                        height: "100%",
                        borderRadius: 5,
                        backgroundColor: "green",
                        transition: "width 0.2s",
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {progress}%
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Validation error */}
            {errors[formField?.field as keyof FormTypes] && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {errors[formField?.field as keyof FormTypes]?.message as string}
              </Typography>
            )}
          </Box>
        );
      }

      default:
        return <></>;
    }
  };

  return (
    <FormControl fullWidth>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <FormLabel sx={{ margin: 0 }}>
          <Box display="flex">
            <LargeScrollTooltip title={formField?.label || ""} disableInteractive>
              <span>
                {formField.label}
              </span>
            </LargeScrollTooltip>
            {isActuallyRequired && (
              <span style={{ color: "#AA0000" }}>* </span>
            )}
            {/* Info icon with tooltip for description */}
            {hasDescription && (
              <Tooltip
                title={formField.description}
                placement="bottom-start"
              >
                <IconButton
                  size="small"
                  sx={{
                    padding: '1px 1px 0px 1px',
                    marginLeft: '4px',
                    color: 'text.secondary'
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </FormLabel>

        {/* Character count displayed to the right side */}
        {formField?.type === "text" && maxLength && (
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              fontSize: "0.75rem",
            }}
          >
            {characterCount}/{maxLength}
          </Typography>
        )}
      </Box>

      <Controller
        name={formField?.field}
        control={control}
        defaultValue={
          formField.type === "checkbox" ? "No" : // ✅ Default checkbox to "No"
          formField.type === "select" ? getInitialSelectValue() : 
          value
        }
        rules={
          formField?.required ||
          formField?.field === "comments" ||
          formField?.field === "previousEVCode"
            ? {
                required: (() => {
                  const operationTypeValue = getOperationTypeValue();
                  const authStatusValue = getAuthStatusValue();

                  // Skip required validation for eVCode when operationType is Insert/Invalidate
                  if (
                    formField?.field === "eVCode" &&
                    (operationTypeValue === "Insert" || operationTypeValue === "Invalidate")
                  ) {
                    return false;
                  }

                  // Comments mandatory when operationType is Nullify
                  if (
                    formField?.field === "comments" &&
                    operationTypeValue === "Nullify"
                  ) {
                    return t("fieldRequired", {
                      field: t(formField?.field),
                    });
                  }

                  // previousEVCode mandatory when authorizationStatus is Transferred/Renewed
                  if (
                    formField?.field === "previousEVCode" &&
                    (authStatusValue === "Valid - Transferred Marketing Authorisation" ||
                     authStatusValue === "Valid - Renewed Marketing Authorisation")
                  ) {
                    return t("fieldRequired", {
                      field: t(formField?.field),
                    });
                  }

                  // Fall back to the field's own configured requirement
                  if (formField?.required) {
                    return t("fieldRequired", {
                      field: t(formField?.field),
                    });
                  }

                  return false;
                })(),
                // Validation - note: async validation removed since loading is handled by parent
                validate: formField.type==="select" ? (value: any) => {
                  return (value !== null && 
                         value !== undefined && 
                         value !== "" &&
                         (typeof value === 'string' ? value.trim() !== '' : true)) ||
                    t("fieldRequired", {
                      field: t(formField?.field),
                    });
                } : undefined,
              }
            : {}
        }
        render={({ field }) => getFieldComponent(field)}
      />
      {/* Show error message below field (for non-TextFields) */}
      {formField?.type !== "text" && 
       formField?.type !== "number" && 
       formField?.type !== "file" && 
       formField?.type !== "date" &&
       formField?.type !== "select" &&
       errors[formField?.field as keyof FormTypes] && (
        <FormHelperText error>
          {errors[formField?.field as keyof FormTypes]?.message}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default FormFieldRenderer;
